import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, CartesianGrid, XAxis, YAxis } from 'recharts';
import { DEFECTS_BY_CATEGORY } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useDateRange } from '../contexts/DateRangeContext';

interface OutcomeItem {
  status: string;
  label: string;
  count: number;
  percentage: number;
}

interface OutcomesData {
  total: number;
  outcomes: OutcomeItem[];
}

interface ActiveUser {
  id: string;
  name: string;
  role: string;
  profileImage: string | null;
  lastLogin: string | null;
  lastActivity: string | null;
  total: number;
  passed: number;
  failed: number;
}

const OUTCOME_COLORS: Record<string, string> = {
  passed: '#10b981',
  failed: '#ef4444',
  pending: '#f59e0b',
};

const fmtTotal = (n: number) => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toString();
};

const getInitials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-600',
  'bg-emerald-100 text-emerald-600',
  'bg-purple-100 text-purple-600',
  'bg-amber-100 text-amber-600',
  'bg-rose-100 text-rose-600',
  'bg-indigo-100 text-indigo-600',
];
const getAvatarColor = (id: string) => {
  const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[index % AVATAR_COLORS.length];
};

const timeAgo = (dateStr: string | null) => {
  if (!dateStr) return 'No activity';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { range } = useDateRange();
  const [outcomesData, setOutcomesData] = useState<OutcomesData | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(true);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(true);
  const [storageData, setStorageData] = useState<{ totalSize: string; totalBytes: number; fileCount: number; byFolder: Record<string, { bytes: number; count: number; size: string }>; database?: { totalSize: string; totalBytes: number; breakdown: { label: string; bytes: number; size: string }[] } } | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    const rangeParam = `?range=${range.key}`;

    const fetchOutcomes = async () => {
      setOutcomesLoading(true);
      try {
        const res = await fetch(`${baseUrl}/api/v1/dashboard/outcomes${rangeParam}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (json.success) setOutcomesData(json.data);
      } catch (err) {
        console.error('Dashboard outcomes fetch failed:', err);
      } finally {
        setOutcomesLoading(false);
      }
    };

    const fetchActiveUsers = async () => {
      setActiveUsersLoading(true);
      try {
        const res = await fetch(`${baseUrl}/api/v1/dashboard/active-users${rangeParam}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (json.success) {
          setActiveUsers(json.data);
        } else {
          console.error('active-users API error:', json.message);
        }
      } catch (err) {
        console.error('Dashboard active-users fetch failed:', err);
      } finally {
        setActiveUsersLoading(false);
      }
    };

    const fetchStorageUsage = async () => {
      setStorageLoading(true);
      try {
        const res = await fetch(`${baseUrl}/api/v1/dashboard/storage-usage`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (json.success) setStorageData(json.data);
      } catch (err) {
        console.error('Dashboard storage fetch failed:', err);
      } finally {
        setStorageLoading(false);
      }
    };

    if (token) {
      fetchOutcomes();
      fetchActiveUsers();
      fetchStorageUsage();
    }
  }, [token, range.key]);

  const inspectionData = outcomesData
    ? outcomesData.outcomes.map((o: OutcomeItem) => ({
        name: o.label,
        value: o.count,
        color: OUTCOME_COLORS[o.status] || '#94a3b8',
        percentage: o.percentage,
      }))
    : [];

  const total = outcomesData?.total ?? 0;
  const passed = outcomesData?.outcomes.find((o: OutcomeItem) => o.status === 'passed');
  const failed = outcomesData?.outcomes.find((o: OutcomeItem) => o.status === 'failed');
  const passRate = passed && total > 0 ? passed.percentage : 0;
  const failRate = failed && total > 0 ? failed.percentage : 0;

  return (
    <div className="mx-auto w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
        <p className="text-sm text-slate-500">Welcome back, {user?.name || 'Inspection Manager'}</p>
      </div>

      {/* KPI Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-5">
        {[
          { label: 'Total Inspections', value: outcomesLoading ? '—' : total.toLocaleString(), color: 'blue', icon: 'inventory_2' },
          { label: 'Pass Rate', value: outcomesLoading ? '—' : `${passRate}%`, color: 'emerald', icon: 'check_circle' },
          { label: 'Fail Rate', value: outcomesLoading ? '—' : `${failRate}%`, color: 'red', icon: 'error' },
          { label: 'Cloud Storage', value: storageLoading ? '—' : (storageData?.totalSize ?? '0 B'), color: 'purple', icon: 'cloud' },
          { label: 'Database', value: storageLoading ? '—' : (storageData?.database?.totalSize ?? '—'), color: 'cyan', icon: 'database' },
        ].map((card, i) => (
          <div key={i} className="relative overflow-hidden rounded-xl bg-white p-6 shadow-sm border border-slate-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <h3 className="mt-2 text-3xl font-bold text-slate-900">{card.value}</h3>
                {card.color === 'purple' && !storageLoading && storageData && (
                  <p className="mt-1 text-xs text-slate-400">{storageData.fileCount.toLocaleString()} files</p>
                )}
              </div>
              <div className={`rounded-lg p-2 ${card.color === 'blue' ? 'bg-blue-50 text-[#136bce]' : card.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' : card.color === 'red' ? 'bg-red-50 text-red-500' : card.color === 'cyan' ? 'bg-cyan-50 text-cyan-600' : 'bg-purple-50 text-purple-600'}`}>
                <span className="material-symbols-outlined">{card.icon}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quality Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Main column: Quality Performance Velocity + Status Distribution / Top Affected SKUs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quality Performance Velocity */}
          <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Quality Performance Velocity</h3>
                <p className="text-xs text-slate-400 mt-0.5">Weekly breakdown of pass vs. defect yields</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-[#136bce]"></div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase">Pass %</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-400"></div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase">Defects %</span>
                </div>
              </div>
            </div>
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[
                  { name: 'Week 1', pass: 85, defects: 15 },
                  { name: 'Week 2', pass: 92, defects: 8 },
                  { name: 'Week 3', pass: 88, defects: 12 },
                  { name: 'Week 4', pass: 94, defects: 6 },
                ]}>
                  <defs>
                    <linearGradient id="colorPass" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#136bce" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#136bce" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }} />
                  <Area type="monotone" dataKey="pass" stroke="#136bce" strokeWidth={3} fillOpacity={1} fill="url(#colorPass)" />
                  <Area type="monotone" dataKey="defects" stroke="#ef4444" strokeWidth={2} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution + Top Affected SKUs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
              <h4 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">donut_small</span>
                Status Distribution
              </h4>
              {(() => {
                const statusData = [
                  { name: 'Passed', value: 1653, color: '#10b981' },
                  { name: 'Failed', value: 636, color: '#ef4444' },
                  { name: 'Rework', value: 254, color: '#136bce' },
                ];
                const statusTotal = statusData.reduce((sum, d) => sum + d.value, 0);
                return (
                  <>
                    <div className="flex items-center gap-6">
                      <div className="w-1/2 h-[160px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={statusData} innerRadius={45} outerRadius={65} paddingAngle={6} dataKey="value">
                              {statusData.map((entry, index) => (
                                <Cell key={`status-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-1/2 space-y-3">
                        {statusData.map((d) => (
                          <div key={d.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: d.color }}></div>
                              <span className="text-xs font-medium text-slate-500">{d.name}</span>
                            </div>
                            <span className="text-xs font-bold text-slate-900">{Math.round((d.value / statusTotal) * 100)}%</span>
                          </div>
                        ))}
                        <div className="pt-2 border-t border-slate-100 mt-2">
                          <p className="text-[10px] text-slate-400 italic">Across all SKU categories</p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Top Affected SKUs */}
            <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
              <h4 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce]">inventory</span>
                Top Affected SKUs
              </h4>
              <div className="space-y-3">
                {[
                  { sku: 'SKU-8811-W', defects: 42, impact: 'High' },
                  { sku: 'SKU-1122-R', defects: 28, impact: 'Medium' },
                  { sku: 'SKU-5544-Y', defects: 15, impact: 'Low' },
                ].map((sku, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div>
                      <p className="text-[11px] font-bold text-slate-900">{sku.sku}</p>
                      <p className="text-[10px] text-slate-500">{sku.defects} defects reported</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                      sku.impact === 'High' ? 'bg-red-100 text-red-700' :
                      sku.impact === 'Medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {sku.impact} Risk
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Executive Summary + Defect Hotspots */}
        <div className="space-y-6">
          {/* Executive Summary */}
          <div className="bg-[#136bce] rounded-xl p-6 text-white shadow-lg shadow-blue-200 relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="text-lg font-bold mb-2">Executive Summary</h3>
              <p className="text-xs text-blue-100 mb-6 leading-relaxed">
                Overall performance is <strong>trending positive</strong>. 8 out of 10 key vendors have exceeded their yield targets this period.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Quality Momentum', desc: "Pass rate increased by 2.4% this month, primarily driven by Facility B's automation upgrades.", icon: 'trending_up', color: 'text-emerald-500' },
                  { title: 'Risk Alert', desc: 'Critical stitching defects in SKU-8811-W have spiked. Supplier corrective action initiated.', icon: 'warning', color: 'text-amber-500' },
                  { title: 'Vendor Optimization', desc: 'Global Textiles remains the top performer with 98.2% yield consistency across all lines.', icon: 'verified', color: 'text-blue-500' },
                ].map((insight, idx) => (
                  <div key={idx} className="flex gap-3 p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0">
                      <span className={`material-symbols-outlined ${insight.color}`}>{insight.icon}</span>
                    </div>
                    <div>
                      <h5 className="text-sm font-bold">{insight.title}</h5>
                      <p className="text-[10px] text-blue-50 mt-1 leading-normal opacity-80">{insight.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute top-10 -left-10 w-20 h-20 bg-white/5 rounded-full blur-2xl"></div>
          </div>

          {/* Defect Hotspots */}
          <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
            <h4 className="font-bold text-slate-800 mb-6 flex items-center gap-2 text-sm uppercase tracking-wider">
              <span className="material-symbols-outlined text-[#136bce]">inventory_2</span>
              Defect Hotspots
            </h4>
            <div className="space-y-5">
              {DEFECTS_BY_CATEGORY.slice(0, 4).map((defect) => (
                <div key={defect.name}>
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight">{defect.name}</span>
                    <span className="text-xs font-bold text-[#136bce]">{defect.percentage}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                    <div
                      className="h-full bg-[#136bce] rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${defect.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
        {/* Overall Inspection Outcomes */}
        <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100 flex flex-col">
          <h3 className="mb-6 text-lg font-bold text-slate-900">Overall Inspection Outcomes</h3>
          {outcomesLoading ? (
            <div className="flex flex-1 items-center justify-center py-16 gap-2 text-slate-400 text-sm">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#136bce]"></div>
              Loading...
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1">
              <div className="h-[240px] w-full mb-8 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={inspectionData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                      {inspectionData.map((entry: { color: string }, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-bold text-slate-900">{fmtTotal(total)}</span>
                  <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Total Items</span>
                </div>
              </div>
              <div className="w-full overflow-hidden rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Count</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {inspectionData.map((d: { name: string; value: number; color: string; percentage: number }) => (
                      <tr key={d.name}>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <span className="mr-2 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }}></span>
                            <span className="font-medium text-slate-900">{d.name}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-slate-600">{d.value.toLocaleString()}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium text-slate-900">{d.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Defects by Category */}
        <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100 flex flex-col">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">Defects by Category</h3>
            <button className="text-sm font-medium text-[#136bce] hover:underline">View Report</button>
          </div>
          <div className="flex-1 flex flex-col gap-6">
            {DEFECTS_BY_CATEGORY.map((defect) => (
              <div key={defect.name}>
                <div className="flex justify-between items-end mb-1.5">
                  <span className="text-sm font-medium text-slate-900">{defect.name}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${defect.severity === 'High' ? 'bg-red-100 text-red-700' : defect.severity === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {defect.severity}
                    </span>
                    <span className="text-sm font-bold text-slate-700">{defect.count}</span>
                  </div>
                </div>
                <div className="relative h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div className="absolute top-0 left-0 h-full bg-[#136bce] transition-all duration-1000 ease-out" style={{ width: `${defect.percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Storage & Database Breakdown */}
      {!storageLoading && storageData && (Object.keys(storageData.byFolder).length > 0 || storageData.database) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
          {/* Cloud Storage Donut */}
          {Object.keys(storageData.byFolder).length > 0 && (() => {
            const STORAGE_CATEGORY_COLORS: Record<string, string> = {
              'Pilot Inspections': '#8b5cf6',
              'Fabric Inspections': '#a78bfa',
              'Line Inspections': '#c4b5fd',
              'Final Inspections': '#7c3aed',
              'User Profile Images': '#f59e0b',
              'Others': '#94a3b8',
            };
            const STORAGE_COLORS = ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#7c3aed'];
            const storageChartData = Object.entries(storageData.byFolder)
              .sort(([, a], [, b]) => b.bytes - a.bytes)
              .map(([folder, info], i) => ({
                name: folder,
                value: info.bytes,
                size: info.size,
                count: info.count,
                color: STORAGE_CATEGORY_COLORS[folder] || STORAGE_COLORS[i % STORAGE_COLORS.length],
                pct: storageData.totalBytes > 0 ? parseFloat(((info.bytes / storageData.totalBytes) * 100).toFixed(1)) : 0,
              }));
            return (
              <div className="rounded-xl bg-white border border-slate-100 shadow-sm p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-6">
                  <div className="rounded-lg p-2 bg-purple-50 text-purple-600">
                    <span className="material-symbols-outlined text-[20px]">cloud</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Cloud Storage</h3>
                    <p className="text-xs text-slate-400">{storageData.fileCount.toLocaleString()} files</p>
                  </div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <div className="h-[200px] w-full relative mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={storageChartData} innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value">
                          {storageChartData.map((entry, index) => (
                            <Cell key={`storage-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => {
                          const item = storageChartData.find(d => d.value === value);
                          return item ? item.size : value;
                        }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-sm font-bold text-slate-900">{storageData.totalSize}</span>
                      <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wider">Total</span>
                    </div>
                  </div>
                  <div className="w-full space-y-2.5">
                    {storageChartData.map((d) => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }}></span>
                          <span className="text-sm text-slate-700">{d.name}</span>
                          <span className="text-[10px] text-slate-400">({d.count} files)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-slate-900">{d.size}</span>
                          <span className="text-xs text-slate-400 w-12 text-right">{d.pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Database Donut */}
          {storageData.database && storageData.database.breakdown.length > 0 && (() => {
            const DB_COLORS = ['#06b6d4', '#0891b2', '#22d3ee', '#155e75', '#f59e0b', '#94a3b8'];
            const dbChartData = storageData.database.breakdown.map((group, i) => ({
              name: group.label,
              value: group.bytes,
              size: group.size,
              color: DB_COLORS[i % DB_COLORS.length],
              pct: storageData.database!.totalBytes > 0 ? parseFloat(((group.bytes / storageData.database!.totalBytes) * 100).toFixed(1)) : 0,
            }));
            return (
              <div className="rounded-xl bg-white border border-slate-100 shadow-sm p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-6">
                  <div className="rounded-lg p-2 bg-cyan-50 text-cyan-600">
                    <span className="material-symbols-outlined text-[20px]">database</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Database</h3>
                    <p className="text-xs text-slate-400">{storageData.database!.totalSize} total</p>
                  </div>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <div className="h-[200px] w-full relative mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={dbChartData} innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value">
                          {dbChartData.map((entry, index) => (
                            <Cell key={`db-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => {
                          const item = dbChartData.find(d => d.value === value);
                          return item ? item.size : value;
                        }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-sm font-bold text-slate-900">{storageData.database!.totalSize}</span>
                      <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wider">Total</span>
                    </div>
                  </div>
                  <div className="w-full space-y-2.5">
                    {dbChartData.map((d) => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: d.color }}></span>
                          <span className="text-sm text-slate-700">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-slate-900">{d.size}</span>
                          <span className="text-xs text-slate-400 w-12 text-right">{d.pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Last Active Inspectors */}
      <div className="rounded-xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Last Active Inspectors</h3>
            <p className="text-xs text-slate-400 mt-0.5">Ranked by most recent inspection activity</p>
          </div>
        </div>

        {activeUsersLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#136bce]"></div>
            Loading inspectors...
          </div>
        ) : activeUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <span className="material-symbols-outlined text-[48px] mb-3 text-slate-200">group</span>
            <p className="text-sm font-medium">No inspector activity found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {activeUsers.map((u: ActiveUser, idx: number) => {
              const passRate = u.total > 0 ? Math.round((u.passed / u.total) * 100) : 0;
              return (
                <div
                  key={u.id}
                  className="relative flex flex-col gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4 hover:border-blue-200 hover:bg-blue-50/20 hover:shadow-sm transition-all group"
                >
                  {/* Rank badge */}
                  {idx < 3 && (
                    <span className={`absolute top-3 right-3 h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-black ${idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-slate-300 text-slate-700' : 'bg-amber-700/70 text-white'}`}>
                      {idx + 1}
                    </span>
                  )}

                  {/* Avatar + Name */}
                  <div className="flex items-center gap-3">
                    {u.profileImage ? (
                      <img
                        src={u.profileImage}
                        alt={u.name}
                        className="h-11 w-11 rounded-full object-cover border-2 border-white shadow-sm shrink-0"
                        onError={(e: any) => {
                          e.currentTarget.style.display = 'none';
                          const sib = e.currentTarget.nextElementSibling as HTMLElement;
                          if (sib) sib.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold border-2 border-white shadow-sm shrink-0 ${getAvatarColor(u.id)}`}
                      style={{ display: u.profileImage ? 'none' : 'flex' }}
                    >
                      {getInitials(u.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{u.name}</p>
                      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider truncate">{u.role}</p>
                    </div>
                  </div>

                  {/* Pass Rate Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pass Rate</span>
                      <span className={`text-xs font-bold ${passRate >= 70 ? 'text-emerald-600' : passRate >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                        {passRate}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${passRate >= 70 ? 'bg-emerald-500' : passRate >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${passRate}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 divide-x divide-slate-200 rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <div className="flex flex-col items-center py-2 px-1">
                      <span className="text-base font-bold text-slate-900">{u.total}</span>
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Total</span>
                    </div>
                    <div className="flex flex-col items-center py-2 px-1">
                      <span className="text-base font-bold text-emerald-600">{u.passed}</span>
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Passed</span>
                    </div>
                    <div className="flex flex-col items-center py-2 px-1">
                      <span className="text-base font-bold text-red-500">{u.failed}</span>
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Failed</span>
                    </div>
                  </div>

                  {/* Last Activity */}
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <span className="material-symbols-outlined text-[14px]">schedule</span>
                    <span>Last active <span className="font-semibold text-slate-500">{timeAgo(u.lastActivity)}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
